import { BaseUser, BaseUserSchema } from "src/stores/models/user";
import { z } from "zod";

const userSchema = z.unknown().transform((value, context): BaseUser => {
  try {
    return BaseUserSchema.validateSync(value);
  } catch {
    context.addIssue({ code: "custom", message: "Invalid user" });
    return z.NEVER;
  }
});

export const qualityEventDocumentReferenceSchema = z.strictObject({
  documentId: z.string(),
  documentVersionId: z.string().nullable(),
  displaySnapshot: z.string(),
});

export type QualityEventDocumentReference = z.infer<
  typeof qualityEventDocumentReferenceSchema
>;

const qualityEventValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  qualityEventDocumentReferenceSchema,
  z.null(),
]);

export type QualityEventValue = z.infer<typeof qualityEventValueSchema>;
export type QualityEventAnswers = Record<string, unknown>;

type QualityEventCondition = { fieldKey: string } & (
  | { equals: string | number | boolean }
  | { contains: string }
  | { oneOf: string[] }
);

export type QualityEventCompletionRequirement = (
  | QualityEventCondition
  | { atLeastOneFields: string[] }
) & { message: string };

type Field = {
  key: string;
  label: string;
  required?: boolean;
  showInOverview?: boolean;
  requiredWhen?: QualityEventCondition;
  showWhen?: QualityEventCondition;
  deadline?: { resolvedWhen?: QualityEventCondition[] };
  tooltip?: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
};

type QualityEventValueField = Field & {
  type:
    | "text"
    | "rich_text"
    | "date"
    | "number"
    | "select"
    | "multi_select"
    | "radio"
    | "checkbox"
    | "user"
    | "device"
    | "file"
    | "document_reference";
};

export type QualityEventField =
  | QualityEventValueField
  | (Field & { type: "approval"; taskTitle?: string })
  | (Field & {
      type: "repeatable";
      createLabel?: string;
      fields: QualityEventValueField[];
      completionRequirements?: QualityEventCompletionRequirement[];
      task?: {
        label?: string;
        labelField?: string;
        titleField: string;
        fallbackTitleField?: string;
        assigneeField: string;
        dueDateField: string;
        completedWhen: QualityEventCondition[];
      };
    });

export type QualityEventStep = {
  key: string;
  title: string;
  taskTitle?: string;
  description?: string;
  fields: QualityEventField[];
  completionRequirements?: QualityEventCompletionRequirement[];
};

export type QualityEventDefinition = {
  key: string;
  name: string;
  description?: string | null;
  definition: {
    assignment?: {
      stepKey: string;
      assigneeField: string;
      dueDateField?: string;
    };
    agentSkills?: Partial<
      Record<"create" | "edit", { id: string; name: string; path: string }>
    >;
    steps: QualityEventStep[];
    editing?: {
      recordOwner: boolean;
      recordResponsible: boolean;
      adminPermission: string;
    };
  };
};

export type QualityEventRepeatableRow = {
  id: string;
  answers: QualityEventAnswers;
};

export type QualityEventApproval = {
  requestId: string;
  approver: string;
  dueDate: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED";
  requestedBy: string;
  requestedAt: string;
  decidedAt: string | null;
  comment: string | null;
};

export type QualityEventApprovalSelection = {
  approver: string | null;
  dueDate: string | null;
};

export const qualityEventRecordSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  qualityEventKey: z.string(),
  title: z.string(),
  version: z.number().int(),
  status: z.enum(["OPEN", "IN_REVIEW", "CLOSED"]),
  closedAt: z.string().nullable(),
  currentStepKey: z.string().nullable(),
  definition: z.custom<QualityEventDefinition>(),
  steps: z.record(
    z.string(),
    z.object({
      answers: z.record(z.string(), z.unknown()),
      updatedAt: z.string().nullable(),
      completedAt: z.string().nullable(),
    })
  ),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: userSchema,
});

export type QualityEventRecord = z.infer<typeof qualityEventRecordSchema>;

const qualityEventProposalAnswerSchema = z.union([
  qualityEventValueSchema,
  z.strictObject({
    approver: z.string().nullable(),
    dueDate: z.string().nullable().optional(),
  }),
  z.array(
    z.strictObject({
      id: z.string().nullable().optional(),
      answers: z.record(z.string(), qualityEventValueSchema),
    })
  ),
]);

const qualityEventStepProposalSchema = z.object({
  answers: z.record(z.string(), qualityEventProposalAnswerSchema).default({}),
});

export const qualityEventMissingRequirementItemSchema = z.object({
  fieldPaths: z.array(z.string()),
  message: z.string(),
});

export const qualityEventMissingRequirementSchema = z.object({
  stepKey: z.string(),
  stepTitle: z.string(),
  requirements: z.array(qualityEventMissingRequirementItemSchema),
});

export type QualityEventMissingRequirement = z.infer<
  typeof qualityEventMissingRequirementSchema
>;

export const qualityEventProposalSchema = z.object({
  qualityEventKey: z.string().min(1),
  stepKey: z.string().min(1),
  id: z.string().uuid().nullish(),
  expectedVersion: z.number().int().positive().nullish(),
  recordId: z
    .string()
    .nullish()
    .transform((value) => value ?? undefined)
    .optional(),
  title: z
    .string()
    .nullish()
    .transform((value) => value ?? undefined)
    .optional(),
  ...qualityEventStepProposalSchema.shape,
  steps: z.record(z.string(), qualityEventStepProposalSchema).nullish(),
  missingRequirements: z.array(qualityEventMissingRequirementSchema).nullish(),
});

export type QualityEventProposal = z.infer<typeof qualityEventProposalSchema>;

export type QualityEventDraft = {
  id: string;
  expectedVersion?: number;
  title: string;
  answers: QualityEventAnswers;
  files: Record<string, File[]>;
};

export const isApprovalSelection = (
  value: unknown
): value is QualityEventApprovalSelection =>
  !!value && typeof value === "object" && "approver" in value;

export const isQualityEventApproval = (
  value: unknown
): value is QualityEventApproval =>
  isApprovalSelection(value) && "requestId" in value && "status" in value;

export const isQualityEventDocumentReference = (
  value: unknown
): value is QualityEventDocumentReference =>
  qualityEventDocumentReferenceSchema.safeParse(value).success;

export const qualityEventRows = (
  value: unknown
): QualityEventRepeatableRow[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      item &&
      typeof item === "object" &&
      typeof item.id === "string" &&
      item.answers &&
      typeof item.answers === "object" &&
      !Array.isArray(item.answers)
  )
    ? value
    : [];

export const qualityEventAuditSchema = z.array(
  z.object({
    id: z.string(),
    occurredAt: z.string(),
    action: z.string(),
    actorUser: userSchema.nullable(),
    actorType: z.string(),
    entityTable: z.string(),
    changes: z.record(z.string(), z.unknown()),
  })
);

export type QualityEventAuditEntry = z.infer<
  typeof qualityEventAuditSchema
>[number];

export type QualityEventApprovalDecision =
  | {
      requestId: string;
      decision: "APPROVED";
      comment: string;
      pin: string;
    }
  | {
      requestId: string;
      decision: "REJECTED";
      comment: string;
    };
